import type {
  FundDividend,
  FundRiskLevel,
  NewFundCommandData,
  PrognosisAssetFraction,
} from '@festina/unitlink-general-types/unitlink'
import { useAuthenticationContext } from '@festina-life/authentication'
import { typeUtils } from '@festina-life/core'
import {
  FormikFieldArray,
  FormikForm,
  FormikPercentageInput,
  FormikSelect,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'

import { useEnumOptions } from '../../../hooks/useEnumOptions'
import { useTime } from '../../../hooks/useTime'
import type { CommandPaletteFormProps } from '../../commandActions/commandPalette/CommandPaletteModalButton'
import { FundDividendFields } from './FundDividendFields'
import { PrognosisAssetFractionFields } from './PrognosisAssetFractionFields'

const validateKey = typeUtils.createValidator<NewFundCommandData>()

export const NewFundForm = (
  props: CommandPaletteFormProps<NewFundCommandData>,
) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { isInTestMode } = useAuthenticationContext()
  const { data: time } = useTime()

  const initialValues: NewFundCommandData = {
    _type: 'NewFundCommandData',
    fromDate: isInTestMode ? time : undefined,
  }

  const fundRiskLevelOptions = useEnumOptions('FundRiskLevel', 'unitlink')

  return (
    <FormikForm initialValues={initialValues} {...props}>
      <FormikTextInput
        name={validateKey('isinKey')}
        label={translate('funds.fundItem.isinKey')}
      />
      <FormikTextInput
        name={validateKey('internalId')}
        label={translate('funds.fundItem.internalId')}
      />
      <FormikTextInput
        name={validateKey('assetManagerId')}
        label={translate('funds.fundItem.assetManagerId')}
      />
      <FormikTextInput
        name={validateKey('name')}
        label={translate('funds.fundItem.name')}
      />
      <FormikTextInput
        name={validateKey('shortName')}
        label={translate('funds.fundItem.shortName')}
      />
      <FormikSelect
        name={validateKey('fundRiskLevel')}
        label={translate('funds.fundItem.fundRiskLevel')}
        shouldShowClearButton={false}
        options={fundRiskLevelOptions}
        renderFn={(x: FundRiskLevel) =>
          translateEnum('unitLink.enum.FundRiskLevel', x)
        }
      />
      <FormikPercentageInput
        name={validateKey('internalCost')}
        label={translate('funds.fundItem.internalCost')}
      />
      <FormikFieldArray
        name={validateKey('fundDividends')}
        label={translate('funds.fundItem.fundDividends')}
        elementLabel={translate('funds.fundDividends.element')}
        elementInitializer={() =>
          ({ _type: 'FundDividend' }) satisfies FundDividend
        }
        ChildElement={FundDividendFields}
      />
      <FormikFieldArray
        name={validateKey('prognosisAssetFractions')}
        label={translate('funds.fundItem.prognosisAssetFractions')}
        elementLabel={translate('funds.prognosisAssetFraction.element')}
        elementInitializer={() =>
          ({ _type: 'PrognosisAssetFraction' }) satisfies PrognosisAssetFraction
        }
        ChildElement={PrognosisAssetFractionFields}
      />
    </FormikForm>
  )
}
